import React, { useState, useEffect, useContext } from 'react';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { createApiService } from '../utils/apiService';
import './TradingPages.css';

const AlgorithmThemeExecutor = () => {
  const { getApiUrl, getServer, isConfigLoaded } = useContext(ServerConfigContext);

  // 날짜/시간 입력 상태
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('15:30');
  const [batchSize, setBatchSize] = useState(30); // 배치당 시간대 개수
  const [concurrentBatches, setConcurrentBatches] = useState(5); // 동시 배치 개수

  // 조회 가능한 날짜 목록
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);

  // 실행 상태
  const [isRunning, setIsRunning] = useState(false);
  const [runningCount, setRunningCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Flush 상태
  const [isFlushing, setIsFlushing] = useState(false);

  // 토스트 상태
  const [toasts, setToasts] = useState([]);

  // 토스트 추가 함수
  const addToast = (message, type = 'info') => {
    const id = Date.now();
    const toast = { id, message, type };
    setToasts(prev => [...prev, toast]);

    // 3초 후 자동 제거
    setTimeout(() => {
      removeToast(id);
    }, 3000);
  };

  // 토스트 제거 함수
  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // 결과 데이터
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);

  // 필터 상태
  const [filters, setFilters] = useState({
    stockName: '',
    stockCodes: [], // 다중 종목코드 필터
    themes: [],     // 다중 테마 필터
    minRate: '',
    maxRate: ''
  });

  // 조회 가능한 날짜 목록을 가져오는 함수
  const fetchAvailableDates = async () => {
    try {
      setLoading(true);
      const server = getServer('stock-api-server');

      if (!server || !server.isActive) {
        console.error('stock-api-server를 찾을 수 없거나 비활성 상태입니다.');
        return;
      }

      const apiService = createApiService(server);
      const dateList = await apiService.get('/api/v1/ranks');

      console.log('📅 조회 가능한 날짜 목록:', dateList);
      setAvailableDates(dateList.sort().reverse()); // 최신순 정렬

      // 기본 날짜 설정 (최근 날짜와 3번째 전 날짜)
      if (dateList.length > 0) {
        const latestDate = dateList[0];
        const formattedLatestDate = `${latestDate.substring(0, 4)}-${latestDate.substring(4, 6)}-${latestDate.substring(6, 8)}`;
        setEndDate(formattedLatestDate);

        // 3번째 전 가능한 날짜 계산 (가능한 날짜 중에서만)
        const startDateIndex = Math.min(2, dateList.length - 1); // 최대 3번째 또는 마지막 날짜
        const startDateString = dateList[startDateIndex];
        const formattedStartDate = `${startDateString.substring(0, 4)}-${startDateString.substring(4, 6)}-${startDateString.substring(6, 8)}`;
        setStartDate(formattedStartDate);
      }
    } catch (error) {
      console.error('❌ 날짜 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 시간을 분으로 변환하는 함수
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 분을 시간으로 변환하는 함수
  const minutesToTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // 실행 시간 목록 생성
  const generateExecutionTimes = () => {
    const times = [];
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    // 시작 날짜부터 종료 날짜까지 반복
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    for (let currentDate = new Date(startDateObj); currentDate <= endDateObj; currentDate.setDate(currentDate.getDate() + 1)) {
      const dateString = currentDate.toISOString().split('T')[0].replace(/-/g, '');

      // 해당 날짜가 조회 가능한 날짜 목록에 있는지 확인
      if (!availableDates.includes(dateString)) {
        continue;
      }

      // 시간 범위 내에서 분 단위로 생성
      for (let minutes = startMinutes; minutes <= endMinutes; minutes++) {
        const timeString = minutesToTime(minutes);
        times.push({
          date: dateString,
          time: timeString,
          dateFormatted: `${dateString.substring(0, 4)}-${dateString.substring(4, 6)}-${dateString.substring(6, 8)}`,
          status: 'waiting' // waiting, running, completed, error
        });
      }
    }

    return times.sort((a, b) => {
      // 날짜와 시간 순으로 정렬
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.time.localeCompare(b.time);
    });
  };

  // 배치 테마 알고리즘 호출 함수 (새로운 batch API 사용)
  const callBatchThemeAlgorithm = async (executions) => {
    try {
      const alertApiUrl = getApiUrl('alert-api-server', '/analysis/batch');

      // 시간 범위 계산 (executions는 이미 정렬된 상태)
      const startExecution = executions[0];
      const endExecution = executions[executions.length - 1];

      const startHour = parseInt(startExecution.time.split(':')[0]);
      const startMinute = parseInt(startExecution.time.split(':')[1]);
      const endHour = parseInt(endExecution.time.split(':')[0]);
      const endMinute = parseInt(endExecution.time.split(':')[1]);

      // 모든 실행이 같은 날짜인지 확인 (배치는 같은 날짜만 처리)
      const uniqueDates = [...new Set(executions.map(ex => ex.date))];
      if (uniqueDates.length > 1) {
        throw new Error(`배치 요청은 같은 날짜만 처리 가능합니다. 날짜: ${uniqueDates.join(', ')}`);
      }

      const requestBody = {
        analysis_type: 'theme',
        analyzed_date: startExecution.date,
        start_hour: startHour,
        start_minute: startMinute,
        end_hour: endHour,
        end_minute: endMinute,
        interval_minute: 1,
        nullim_days: 3,
        send_telegram: false
      };

      console.log(`🚀 배치 테마 알고리즘 호출: ${executions.length}개 시간대`, {
        date: startExecution.date,
        timeRange: `${startHour}:${startMinute.toString().padStart(2, '0')} ~ ${endHour}:${endMinute.toString().padStart(2, '0')}`,
        intervalMinute: 1,
        requestBody
      });

      const response = await fetch(alertApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success && result.data && result.data.time_slots) {
        // 배치 응답의 time_slots 배열에서 각 시간대별 결과 처리
        const allResults = [];

        result.data.time_slots.forEach(timeSlot => {
          if (timeSlot.success && timeSlot.results && timeSlot.results.length > 0) {
            // analyzed_time을 HH:MM 형식으로 변환 (예: "090000" -> "09:00")
            const timeStr = timeSlot.analyzed_time;
            const formattedTime = `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}`;

            const timeSlotResults = timeSlot.results.map(item => ({
              ...item,
              execution_date: startExecution.dateFormatted,
              execution_time: formattedTime,
              datetime: `${startExecution.dateFormatted} ${formattedTime}`,
              themes: item.theme ? [item.theme] : [] // API의 theme 키값을 배열로 변환
            }));

            allResults.push(...timeSlotResults);
          }
        });

        console.log(`✅ 배치 완료: ${result.data.total_executions}개 시간대, ${allResults.length}개 결과`, {
          totalExecutions: result.data.total_executions,
          successCount: result.data.success_count,
          failureCount: result.data.failure_count,
          cachedCount: result.data.cached_count,
          executionTimeMs: result.data.execution_time_ms
        });
        return { executions, results: allResults, success: true };
      } else {
        console.warn(`⚠️ 배치 결과 없음:`, result);
        return { executions, results: [], success: false, error: 'No results' };
      }
    } catch (error) {
      console.error(`❌ 배치 실패:`, error);
      return { executions, results: [], success: false, error: error.message };
    }
  };

  // 시간대 그룹을 생성하는 함수 (배치당 시간대 개수 기반)
  const createBatchGroups = (executions) => {
    const batchGroups = [];

    // concurrentCount 개수씩 그룹으로 나누기
    for (let i = 0; i < executions.length; i += concurrentCount) {
      batchGroups.push(executions.slice(i, i + concurrentCount));
    }

    // 최대 5개 배치만 동시 실행하도록 제한
    const MAX_CONCURRENT_BATCHES = 5;
    if (batchGroups.length <= MAX_CONCURRENT_BATCHES) {
      return batchGroups;
    }

    // 배치가 5개보다 많으면 5개씩 순차 실행하기 위해 그룹화
    const finalGroups = [];
    for (let i = 0; i < batchGroups.length; i += MAX_CONCURRENT_BATCHES) {
      finalGroups.push(batchGroups.slice(i, i + MAX_CONCURRENT_BATCHES));
    }

    return finalGroups[0]; // 첫 번째 그룹만 반환 (순차 실행은 executeBatch에서 처리)
  };

  // 배치 실행 함수 (최대 5개 배치 동시 실행)
  const executeBatch = async (executions) => {
    setIsRunning(true);
    setRunningCount(0);
    setCompletedCount(0);
    setTotalCount(executions.length);
    setResults([]);

    // 날짜별로 그룹화한 후 batchSize 개수씩 배치 그룹으로 분할
    const allBatchGroups = [];

    // 날짜별로 시간대 그룹화
    const executionsByDate = {};
    executions.forEach(execution => {
      if (!executionsByDate[execution.date]) {
        executionsByDate[execution.date] = [];
      }
      executionsByDate[execution.date].push(execution);
    });

    // 각 날짜별로 배치 그룹 생성
    Object.keys(executionsByDate).forEach(date => {
      const dateExecutions = executionsByDate[date];

      // 해당 날짜의 시간대를 batchSize 개수씩 나누기
      for (let i = 0; i < dateExecutions.length; i += batchSize) {
        const batchGroup = dateExecutions.slice(i, i + batchSize);
        allBatchGroups.push(batchGroup);
      }
    });

    console.log(`📊 총 ${executions.length}개 시간대를 ${allBatchGroups.length}개 배치로 분할`, {
      totalTimeSlots: executions.length,
      batchCount: allBatchGroups.length,
      timeSlotsPerBatch: batchSize,
      concurrentBatches: concurrentBatches,
      groupSizes: allBatchGroups.map(group => group.length)
    });

    // 설정된 개수만큼 배치 동시 실행
    for (let i = 0; i < allBatchGroups.length; i += concurrentBatches) {
      const currentBatchGroup = allBatchGroups.slice(i, i + concurrentBatches);

      console.log(`🎯 배치 그룹 ${Math.floor(i/concurrentBatches) + 1} 실행: ${currentBatchGroup.length}개 배치 동시 처리`);

      setRunningCount(currentBatchGroup.reduce((sum, group) => sum + group.length, 0));

      const batchPromises = currentBatchGroup.map((batchGroup, batchIndex) => {
        console.log(`   - 배치 ${batchIndex + 1}: ${batchGroup.length}개 시간대`);
        return callBatchThemeAlgorithm(batchGroup);
      });

      try {
        const batchResults = await Promise.all(batchPromises);

        // 현재 배치 그룹의 결과 수집
        const currentBatchResults = [];

        // 현재 그룹의 배치 결과 처리
        for (const batchResult of batchResults) {
          console.log(`📋 배치 결과 처리:`, {
            success: batchResult.success,
            resultsCount: batchResult.results ? batchResult.results.length : 0,
            executionsCount: batchResult.executions ? batchResult.executions.length : 0
          });

          if (batchResult.success && batchResult.results.length > 0) {
            console.log(`  ✅ ${batchResult.results.length}개 결과를 currentBatchResults에 추가`);
            currentBatchResults.push(...batchResult.results);
          }

          // 해당 배치의 시간대 개수만큼 완료 카운트 증가
          const processedCount = batchResult.executions ? batchResult.executions.length : 0;
          console.log(`  📊 완료 카운트 증가: ${processedCount}개`);
          setCompletedCount(prev => {
            console.log(`  📈 완료 카운트: ${prev} → ${prev + processedCount}`);
            return prev + processedCount;
          });
        }

        setRunningCount(0);

        console.log(`🔄 현재 배치 그룹 결과: ${currentBatchResults.length}개`);

        // 현재 배치 그룹 결과를 상태에 누적 업데이트
        if (currentBatchResults.length > 0) {
          setResults(prevResults => {
            console.log(`  🔄 이전 결과: ${prevResults.length}개, 추가할 결과: ${currentBatchResults.length}개`);

            // 새로운 결과를 기존 결과에 누적
            const combinedResults = [...prevResults, ...currentBatchResults];

            // 중복 제거 (datetime + stock_code 기준)
            const uniqueResults = combinedResults.filter((result, index, arr) =>
              index === arr.findIndex(r => r.datetime === result.datetime && r.stock_code === result.stock_code)
            );

            // 시간순 정렬
            const finalResults = uniqueResults.sort((a, b) => a.datetime.localeCompare(b.datetime));

            console.log(`  📋 최종 결과: ${finalResults.length}개 (중복 제거 후)`);
            return finalResults;
          });
        }

        // 다음 배치 그룹 전 잠시 대기 (서버 과부하 방지)
        if (i + concurrentBatches < allBatchGroups.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

      } catch (error) {
        console.error('❌ 배치 그룹 실행 중 오류:', error);
        setRunningCount(0);
      }
    }

    setIsRunning(false);
    console.log(`🎉 모든 배치 실행 완료!`);
  };

  // 실행 버튼 클릭 핸들러
  const handleExecute = () => {
    if (!startDate || !endDate || !startTime || !endTime) {
      alert('모든 날짜와 시간을 입력해주세요.');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('시작 날짜가 종료 날짜보다 늦을 수 없습니다.');
      return;
    }

    const executions = generateExecutionTimes();
    if (executions.length === 0) {
      alert('실행할 시간이 없습니다. 날짜와 시간을 확인해주세요.');
      return;
    }

    console.log(`🎬 실행 시작: ${executions.length}개 작업 예정`);
    executeBatch(executions);
  };

  // Flush 버튼 클릭 핸들러
  const handleFlush = async () => {
    if (!startDate || !endDate || !startTime || !endTime) {
      addToast('모든 날짜와 시간을 입력해주세요.', 'error');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      addToast('시작 날짜가 종료 날짜보다 늦을 수 없습니다.', 'error');
      return;
    }

    const executions = generateExecutionTimes();
    if (executions.length === 0) {
      addToast('플러시할 시간이 없습니다. 날짜와 시간을 확인해주세요.', 'error');
      return;
    }

    // 날짜별로 그룹화
    const dateGroups = {};
    executions.forEach(exec => {
      if (!dateGroups[exec.date]) {
        dateGroups[exec.date] = [];
      }
      dateGroups[exec.date].push(exec);
    });

    const uniqueDates = Object.keys(dateGroups);
    const totalTimePoints = executions.length;

    // 확인 대화상자
    const confirmMessage = `캐시를 삭제하시겠습니까?\n\n📊 총 ${totalTimePoints}개 시간점의 캐시가 삭제됩니다.\n📅 대상 날짜: ${uniqueDates.length}일 (${uniqueDates.join(', ')})\n⏰ 기간: ${startDate} ${startTime} ~ ${endDate} ${endTime}\n\n※ 이 작업은 되돌릴 수 없습니다.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsFlushing(true);
    addToast(`🧹 캐시 삭제 시작: ${uniqueDates.length}개 날짜 처리`, 'info');

    try {
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);

      let successCount = 0;
      let failCount = 0;
      const results = [];

      // 각 날짜별로 flush API 호출
      for (const date of uniqueDates) {
        try {
          const requestBody = {
            analysis_type: 'theme',
            analyzed_date: date,
            start_hour: startHour,
            start_minute: startMinute,
            end_hour: endHour,
            end_minute: endMinute,
            interval_minute: 30,
            nullim_days: 3
          };

          console.log(`🧹 ${date} 캐시 플러시 시작:`, requestBody);

          const alertApiUrl = getApiUrl('alert-api-server', '/analysis/batch/flush');
          const response = await fetch(alertApiUrl, {
            method: 'POST',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });

          const result = await response.json();

          if (result.success) {
            successCount++;
            console.log(`✅ ${date} 캐시 플러시 성공:`, result);
            results.push(`${date}: ${result.message}`);
          } else {
            failCount++;
            console.error(`❌ ${date} 캐시 플러시 실패:`, result);
            results.push(`${date}: 실패 - ${result.message || '알 수 없는 오류'}`);
          }

        } catch (error) {
          failCount++;
          console.error(`❌ ${date} 캐시 플러시 중 오류:`, error);
          results.push(`${date}: 오류 - ${error.message}`);
        }
      }

      // 결과 토스트 표시
      if (successCount > 0 && failCount === 0) {
        addToast(`✅ 모든 캐시 삭제 완료! (${successCount}개 날짜)`, 'success');
      } else if (successCount > 0 && failCount > 0) {
        addToast(`⚠️ 캐시 삭제 부분 완료: 성공 ${successCount}개, 실패 ${failCount}개`, 'warning');
      } else {
        addToast(`❌ 캐시 삭제 실패: ${failCount}개 날짜에서 모두 실패`, 'error');
      }

      // 상세 결과 로그
      console.log('🧹 Flush 완료 결과:', {
        총날짜: uniqueDates.length,
        성공: successCount,
        실패: failCount,
        상세결과: results
      });

    } catch (error) {
      console.error('❌ 캐시 플러시 중 전체 오류:', error);
      addToast(`❌ 캐시 삭제 중 오류가 발생했습니다: ${error.message}`, 'error');
    } finally {
      setIsFlushing(false);
    }
  };

  // 컴포넌트 초기화
  useEffect(() => {
    if (isConfigLoaded) {
      fetchAvailableDates();
    }
  }, [isConfigLoaded]);

  // 날짜가 조회 가능한지 확인하는 함수
  const isDateAvailable = (dateString) => {
    const formattedDate = dateString.replace(/-/g, '');
    return availableDates.includes(formattedDate);
  };

  // 필터 적용 함수
  const applyFilters = (data) => {
    return data.filter(result => {
      // 종목명 필터
      if (filters.stockName && !result.stock_name.toLowerCase().includes(filters.stockName.toLowerCase())) {
        return false;
      }

      // 다중 종목코드 필터
      if (filters.stockCodes.length > 0 && !filters.stockCodes.includes(result.stock_code)) {
        return false;
      }

      // 다중 테마 필터
      if (filters.themes.length > 0) {
        const hasMatchingTheme = result.themes?.some(theme =>
          filters.themes.some(filterTheme => theme && theme.toLowerCase().includes(filterTheme.toLowerCase()))
        );
        if (!hasMatchingTheme) {
          return false;
        }
      }

      // 등락률 범위 필터
      if (filters.minRate && result.diff < parseFloat(filters.minRate)) {
        return false;
      }

      if (filters.maxRate && result.diff > parseFloat(filters.maxRate)) {
        return false;
      }

      return true;
    });
  };

  // 필터 변경 핸들러
  const handleFilterChange = (filterKey, value) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
    setFilteredResults(applyFilters(results));
  };

  // 종목코드 필터 추가/제거
  const toggleStockCodeFilter = (stockCode) => {
    setFilters(prevFilters => {
      const newStockCodes = prevFilters.stockCodes.includes(stockCode)
        ? prevFilters.stockCodes.filter(code => code !== stockCode)
        : [...prevFilters.stockCodes, stockCode];

      return { ...prevFilters, stockCodes: newStockCodes };
    });
  };

  // 테마 필터 추가/제거
  const toggleThemeFilter = (theme) => {
    if (!theme) return; // 빈 테마는 무시

    setFilters(prevFilters => {
      const newThemes = prevFilters.themes.includes(theme)
        ? prevFilters.themes.filter(t => t !== theme)
        : [...prevFilters.themes, theme];

      return { ...prevFilters, themes: newThemes };
    });
  };

  // 개별 필터 제거
  const removeStockCodeFilter = (stockCode) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      stockCodes: prevFilters.stockCodes.filter(code => code !== stockCode)
    }));
  };

  const removeThemeFilter = (theme) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      themes: prevFilters.themes.filter(t => t !== theme)
    }));
  };

  // 필터 초기화
  const resetFilters = () => {
    setFilters({
      stockName: '',
      stockCodes: [],
      themes: [],
      minRate: '',
      maxRate: ''
    });
    setFilteredResults(results);
  };

  // results가 변경될 때마다 필터 적용
  useEffect(() => {
    setFilteredResults(applyFilters(results));
  }, [results, filters]);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🎯 테마 알고리즘 배치 실행</h1>
        <p>테마 분석 알고리즘을 시간 범위별로 배치 실행합니다</p>
      </div>

      {/* 실행 설정 패널 */}
      <div className="algorithm-control-panel">
        <div className="control-section">
          <h3>📅 날짜 설정</h3>
          <div className="date-inputs">
            <div className="input-group">
              <label>시작 날짜</label>
              <select
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              >
                <option value="">날짜 선택</option>
                {availableDates.map(date => {
                  const formattedDate = `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`;
                  return (
                    <option key={date} value={formattedDate}>
                      {formattedDate}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="input-group">
              <label>종료 날짜</label>
              <select
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
              >
                <option value="">날짜 선택</option>
                {availableDates.map(date => {
                  const formattedDate = `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`;
                  return (
                    <option key={date} value={formattedDate}>
                      {formattedDate}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        <div className="control-section">
          <h3>🕒 시간 설정</h3>
          <div className="time-inputs">
            <div className="input-group">
              <label>시작 시간</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                min="09:00"
                max="15:30"
                className="time-input"
              />
            </div>
            <div className="input-group">
              <label>종료 시간</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                min="09:00"
                max="15:30"
                className="time-input"
              />
            </div>
          </div>
        </div>

        <div className="control-section">
          <h3>⚡ 실행 설정</h3>
          <div className="execution-inputs">
            <div className="input-group">
              <label>배치당 시간대 개수</label>
              <input
                type="number"
                value={batchSize}
                onChange={(e) => setBatchSize(Math.max(10, Math.min(100, parseInt(e.target.value) || 10)))}
                min="10"
                max="100"
                className="number-input"
              />
              <small>각 배치 API 요청에서 처리할 시간대 개수 (10-100개)</small>
            </div>
            <div className="input-group">
              <label>동시 배치 개수</label>
              <input
                type="number"
                value={concurrentBatches}
                onChange={(e) => setConcurrentBatches(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                min="1"
                max="10"
                className="number-input"
              />
              <small>동시에 실행할 배치 API 요청 개수 (1-10개)</small>
            </div>
          </div>
        </div>

        <div className="control-section">
          <div className="execution-summary">
            <p>📊 <strong>예상 실행 횟수:</strong> {availableDates.length > 0 ? generateExecutionTimes().length : 0}회 | ⚡ <strong>배치 처리:</strong> {concurrentBatches}개 동시 배치 (배치당 최대 {batchSize}개)</p>
            <small style={{color: '#6c757d', display: 'block', marginTop: '8px'}}>
              🚀 {concurrentBatches}개 배치를 동시 실행하며, 각 배치는 최대 {batchSize}개 시간대를 한 번에 처리합니다
            </small>
          </div>

          <div className="button-group">
            <button
              onClick={handleExecute}
              className={`execute-btn ${isRunning ? 'running' : ''}`}
              disabled={isRunning || isFlushing || loading || !startDate || !endDate}
            >
              {isRunning ? '실행 중...' : '🚀 실행'}
            </button>

            <button
              onClick={handleFlush}
              className={`flush-btn ${isFlushing ? 'flushing' : ''}`}
              disabled={isRunning || isFlushing || loading || !startDate || !endDate}
              title="입력한 시간 범위의 모든 캐시를 삭제합니다"
            >
              {isFlushing ? '🧹 삭제 중...' : '🧹 Flush'}
            </button>
          </div>
        </div>
      </div>

      {/* 실행 상태 */}
      {isRunning && (
        <div className="execution-status">
          <div className="status-header">
            <h3>배치 실행 상태</h3>
            <div className="progress-info">
              <span>처리된 시간대: {completedCount}/{totalCount}</span>
              <span>실행 중: {runningCount > 0 ? `${runningCount}개 시간대 처리 중` : '대기'}</span>
            </div>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
          <div className="batch-status">
            <small style={{color: '#6c757d'}}>
              ⚡ 배치 API를 통해 여러 시간대를 한 번에 처리하고 있습니다
            </small>
          </div>
        </div>
      )}

      {/* 결과 테이블 */}
      {results.length > 0 && (
        <div className="results-section">
          <div className="results-header">
            <h3>📈 분석 결과</h3>
            <div className="results-summary">
              <span>총 {results.length}개 종목 | 필터 적용: {filteredResults.length}개</span>
            </div>
          </div>

          {/* 필터 컨트롤 */}
          <div className="results-filters">
            {/* 활성 필터 태그 표시 */}
            {(filters.stockCodes.length > 0 || filters.themes.length > 0 || filters.stockName || filters.minRate || filters.maxRate) && (
              <div className="active-filters">
                <div className="filter-tags">
                  {/* 종목코드 필터 태그 */}
                  {filters.stockCodes.map(stockCode => (
                    <span key={stockCode} className="filter-tag stock-code-tag">
                      {stockCode}
                      <button onClick={() => removeStockCodeFilter(stockCode)} className="remove-tag">×</button>
                    </span>
                  ))}
                  {/* 테마 필터 태그 */}
                  {filters.themes.map(theme => (
                    <span key={theme} className="filter-tag theme-tag">
                      {theme}
                      <button onClick={() => removeThemeFilter(theme)} className="remove-tag">×</button>
                    </span>
                  ))}
                  {/* 종목명 필터 태그 */}
                  {filters.stockName && (
                    <span className="filter-tag name-tag">
                      종목명: {filters.stockName}
                      <button onClick={() => handleFilterChange('stockName', '')} className="remove-tag">×</button>
                    </span>
                  )}
                  {/* 등락률 범위 필터 태그 */}
                  {(filters.minRate || filters.maxRate) && (
                    <span className="filter-tag rate-tag">
                      등락률: {filters.minRate || '-∞'}% ~ {filters.maxRate || '∞'}%
                      <button onClick={() => { handleFilterChange('minRate', ''); handleFilterChange('maxRate', ''); }} className="remove-tag">×</button>
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="filter-row">
              <div className="filter-group">
                <label>종목명</label>
                <input
                  type="text"
                  placeholder="검색어 입력"
                  value={filters.stockName}
                  onChange={(e) => handleFilterChange('stockName', e.target.value)}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>등락률 범위</label>
                <div className="range-inputs">
                  <input
                    type="number"
                    placeholder="최소"
                    step="0.1"
                    value={filters.minRate}
                    onChange={(e) => handleFilterChange('minRate', e.target.value)}
                    className="filter-input range-input"
                  />
                  <span>~</span>
                  <input
                    type="number"
                    placeholder="최대"
                    step="0.1"
                    value={filters.maxRate}
                    onChange={(e) => handleFilterChange('maxRate', e.target.value)}
                    className="filter-input range-input"
                  />
                </div>
              </div>
              <button onClick={resetFilters} className="reset-filters-btn">
                전체 초기화
              </button>
            </div>
            <div className="filter-info">
              <small>💡 <strong>팁:</strong> 표에서 종목코드나 테마를 클릭하면 해당 필터가 추가됩니다</small>
            </div>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>실행일시</th>
                  <th>종목코드</th>
                  <th>종목명</th>
                  <th>현재가</th>
                  <th>등락률</th>
                  <th>순위</th>
                  <th>테마</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((result, index) => (
                  <tr key={`result-${index}-${result.stock_code}-${result.datetime}`}>
                    <td className="datetime">{result.datetime}</td>
                    <td
                      className="stock-code clickable-filter"
                      onClick={() => toggleStockCodeFilter(result.stock_code)}
                      title={`클릭하여 ${result.stock_code} 필터 ${filters.stockCodes.includes(result.stock_code) ? '제거' : '추가'}`}
                      data-active={filters.stockCodes.includes(result.stock_code)}
                    >
                      {result.stock_code}
                    </td>
                    <td className="stock-name">{result.stock_name}</td>
                    <td className="price">{result.price ? result.price.toLocaleString() : '-'}원</td>
                    <td className={`rate ${result.diff > 0 ? 'positive' : result.diff < 0 ? 'negative' : 'neutral'}`}>
                      {result.diff > 0 ? '+' : ''}{result.diff ? result.diff.toFixed(2) : '0.00'}%
                    </td>
                    <td className="rank">{result.rank}위</td>
                    <td className="themes">
                      {result.themes && result.themes.length > 0 ? (
                        <div className="theme-labels">
                          {result.themes.map((theme, themeIndex) => (
                            <span
                              key={themeIndex}
                              className={`theme-label clickable-filter ${filters.themes.includes(theme) ? 'active-filter' : ''}`}
                              onClick={() => toggleThemeFilter(theme)}
                              title={`클릭하여 ${theme} 필터 ${filters.themes.includes(theme) ? '제거' : '추가'}`}
                            >
                              {theme}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-theme">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 조회 가능한 날짜 표시 */}
      {availableDates.length > 0 && (
        <div className="available-dates-section">
          <h4>📅 조회 가능한 날짜 ({availableDates.length}개)</h4>
          <div className="available-dates">
            {availableDates.slice(0, 10).map(date => (
              <span key={date} className="date-badge">
                {date.substring(0, 4)}-{date.substring(4, 6)}-{date.substring(6, 8)}
              </span>
            ))}
            {availableDates.length > 10 && <span className="more-dates">... 외 {availableDates.length - 10}개</span>}
          </div>
        </div>
      )}

      {/* 토스트 컨테이너 */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
            onClick={() => removeToast(toast.id)}
          >
            <span className="toast-message">{toast.message}</span>
            <button className="toast-close" onClick={() => removeToast(toast.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AlgorithmThemeExecutor;